import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import FlightCalendar from '../components/FlightCalendar';
import { useBusiness } from '../context/BusinessContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import { money, date } from '../lib/format';

const METRICS = [
  { key: 'ar_outstanding', label: 'Outstanding AR' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'revenue_mtd', label: 'Revenue collected, MTD' },
  { key: 'upcoming_billings', label: 'Upcoming contract billings' },
];

export default function Dashboard() {
  const { activeId, activeBusiness } = useBusiness();
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const isFlightBiz = activeBusiness?.short === 'B3';
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flights, setFlights] = useState([]);
  const [w9Pending, setW9Pending] = useState(0);
  const [apprReady, setApprReady] = useState(0);
  const [agrAwaiting, setAgrAwaiting] = useState(0);
  const [stripeStatus, setStripeStatus] = useState(null);

  useEffect(() => {
    api(`/businesses/${activeId}/stripe-status`).then(setStripeStatus).catch(() => setStripeStatus(null));
  }, [activeId]);

  useEffect(() => {
    if (user?.role !== 'admin') return;
    api('/w9/pending-count').then((d) => setW9Pending(d?.count || 0)).catch(() => setW9Pending(0));
  }, [user]);

  useEffect(() => {
    if (can('expenses.view')) api('/approvals/counts').then((d) => setApprReady(d?.ready || 0)).catch(() => setApprReady(0));
    if (can('contracts.view')) api('/agreements/counts').then((d) => setAgrAwaiting(d?.awaiting || 0)).catch(() => setAgrAwaiting(0));
  }, [can]);

  useEffect(() => {
    if (!isFlightBiz) { setFlights([]); return; }
    let cancelled = false;
    api(`/jobs?business_id=${activeId}`)
      .then((d) => { if (!cancelled) setFlights((Array.isArray(d) ? d : []).filter((f) => f.flight_date)); })
      .catch(() => { if (!cancelled) setFlights([]); });
    return () => { cancelled = true; };
  }, [activeId, isFlightBiz]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api(`/dashboard?business_id=${activeId}`)
      .then((data) => {
        if (cancelled) return;
        setSummary(data?.metrics || null);
        setRecent(data?.recent_invoices || []);
      })
      .catch(() => {
        if (cancelled) return;
        // Backend not connected yet — show empty states rather than failing hard.
        setSummary(null);
        setRecent([]);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [activeId]);

  const columns = [
    { key: 'invoice_number', header: 'Invoice' },
    { key: 'customer', header: 'Customer', render: (r) => r.customer_name || '—' },
    { key: 'issue_date', header: 'Issued', render: (r) => date(r.issue_date) },
    { key: 'total', header: 'Total', align: 'right', render: (r) => <span className="num">{money(r.total)}</span> },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ];

  return (
    <>
      <PageHeader title="Dashboard" subtitle={activeBusiness?.name}
        actions={stripeStatus?.enabled && (
          <button
            className={`env-pill env-pill--${stripeStatus.livemode ? 'live' : 'test'} env-pill--btn`}
            onClick={() => { if (user?.role === 'admin') navigate('/settings/payments'); }}
            title={user?.role === 'admin' ? 'Stripe environment — click to manage' : 'Stripe environment'}
          >
            ● Stripe {stripeStatus.livemode ? 'Live' : 'Test'}
          </button>
        )} />

      {agrAwaiting > 0 && (
        <button className="w9-banner" onClick={() => navigate('/agreements')}>
          <span className="w9-banner__dot" />
          {agrAwaiting} {agrAwaiting === 1 ? 'agreement' : 'agreements'} awaiting customer signature
          <span className="w9-banner__go">Open →</span>
        </button>
      )}

      {apprReady > 0 && (
        <button className="w9-banner" onClick={() => navigate('/approvals')}>
          <span className="w9-banner__dot" />
          {apprReady} approved {apprReady === 1 ? 'purchase' : 'purchases'} ready to order
          <span className="w9-banner__go">Open →</span>
        </button>
      )}

      {w9Pending > 0 && (
        <button className="w9-banner" onClick={() => navigate('/w9')}>
          <span className="w9-banner__dot" />
          {w9Pending} W-9 {w9Pending === 1 ? 'request' : 'requests'} awaiting review
          <span className="w9-banner__go">Review →</span>
        </button>
      )}


      <div className="metrics">
        {METRICS.map((m) => (
          <div className="metric" key={m.key}>
            <span className="metric__label">{m.label}</span>
            <span className="metric__value num">
              {summary && summary[m.key] != null ? money(summary[m.key]) : '—'}
            </span>
          </div>
        ))}
      </div>

      {isFlightBiz && (
        <section className="panel">
          <div className="panel__head">
            <h2 className="panel__title">Flight schedule</h2>
            <button className="btn btn--small" onClick={() => navigate('/jobs')}>Open flights</button>
          </div>
          <FlightCalendar flights={flights} onSelect={() => navigate('/jobs')} />
        </section>
      )}

      <section className="panel">
        <div className="panel__head">
          <h2 className="panel__title">Recent invoices</h2>
        </div>
        <DataTable
          columns={columns}
          rows={recent}
          loading={loading}
          error={error}
          getRowKey={(r) => r.id}
          empty="No invoices yet. Once the API is connected, recent activity shows here."
        />
      </section>
    </>
  );
}
